import { Directive, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { appSettings } from '@macs-frontend/core/utils/app-settings';
import { toIO, toRXWithOptions } from '@macs-frontend/core/utils/mobx';
import { ToValues } from '@macs-frontend/web-macs/shared/pipes/to.pipe';
import { function as F } from 'fp-ts';
import * as mobx from 'mobx';
import { Newtype } from 'newtype-ts/es6';
import { box, FormControlState, FormGroupState, unbox } from 'ngrx-forms';
// libs
import { MonoTypeOperatorFunction, Observable, Subject, BehaviorSubject } from 'rxjs';
import * as rx from 'rxjs/operators';
import { numberConverter } from '@macs-frontend/features/shared/utils/number.converter';
import { select, Store, Selector } from '@ngrx/store';

@Directive()
/* eslint-disable-next-line @angular-eslint/directive-class-suffix */
export abstract class BaseComponent implements OnDestroy {
  // Permission = Permission;
  readonly destroy$ = new Subject<void>();
  readonly disposers: mobx.IReactionDisposer[] = [];
  readonly numberConverter = numberConverter;

  /**
   * Creates rxjs's take until function and will trigger destroy on ngOnDestroy.
   */
  readonly takeUntilDestroy: <T>() => MonoTypeOperatorFunction<T> = () =>
    rx.takeUntil(this.destroy$);

  /**
   * Converts mobx observable field to rxjs's Observable.
   */

  readonly toRXWithOptions = toRXWithOptions(this);

  /**
   * Converts mobx observable field to rxjs's Observable.
   */
  readonly toRX = <K extends keyof this>(key: K): Observable<this[K]> =>
    F.pipe(key, this.toRXWithOptions());

  public box = box;
  public unbox = unbox;

  /**
   * Mobx reaction wrapper that will run disposer on ngOnDestroy.
   */
  readonly reaction = <T>(
    expression: (r: mobx.IReactionPublic) => T,
    effect: (arg: T, prev: T | undefined, r: mobx.IReactionPublic) => void,
    opts?: mobx.IReactionOptions<T, boolean>,
  ): mobx.IReactionDisposer => {
    const disposer = mobx.reaction(expression, effect, opts);

    this.disposers.push(disposer);

    return disposer;
  };

  /**
   * Mobx autorun wrapper that will run disposer on ngOnDestroy.
   */
  readonly autorun: typeof mobx.autorun = (
    view: (r: mobx.IReactionPublic) => any,
    opts?: mobx.IAutorunOptions,
  ): mobx.IReactionDisposer => {
    const disposer = mobx.autorun(view, opts);

    this.disposers.push(disposer);

    return disposer;
  };

  readonly toIO = toIO(this);

  readonly appSettings = appSettings;

  trackControl(index: number, control: any) {
    return control?.uuid || control?.key || control?.id || index;
  }

  trackIndex(index: number) {
    return index;
  }

  isInvalid(control: FormGroupState<Newtype<any, any>> | FormControlState<any>): boolean {
    return control.isDirty && control.isInvalid;
  }

  isValid(control: FormGroupState<Newtype<any, any>> | FormControlState<any>): boolean {
    return !this.isInvalid(control);
  }

  ngOnDestroy() {
    this.disposers.forEach(x => typeof x === 'function' && x());
    this.destroy$.next();
    this.destroy$.complete();
  }

  to<T extends keyof ToValues>(value: ToValues[T]['from'], _type: T): ToValues[T]['to'] {
    return value as unknown as ToValues[T]['to'];
  }

  readonly console = console;

  selectorToBehaviourSubject<T>(
    selector: Selector<any, T>,
    defaultValue: T,
  ): BehaviorSubject<T> {
    const subject = new BehaviorSubject<T>(defaultValue);

    const store$: Store<any> | undefined = (this as any).store$;

    if (!store$) {
      throw new Error('store not found');
    }

    const cdr: ChangeDetectorRef | undefined = (this as any).cdr;

    if (!cdr) {
      throw new Error('cdr not found');
    }

    store$
      .pipe(
        select(selector),
        rx.takeUntil(this.destroy$),
        rx.tap(() => cdr.markForCheck()),
      )
      .subscribe(subject);

    return subject;
  }

  observableToBehaviourSubject<T, U extends T | undefined = undefined>(
    observable: Observable<T>,
    defaultValue?: U,
  ): U extends undefined ? BehaviorSubject<T | undefined> : BehaviorSubject<T> {
    const subject = new BehaviorSubject<T | undefined>(defaultValue);

    const cdr: ChangeDetectorRef | undefined = (this as any).cdr;

    if (!cdr) {
      throw new Error('cdr not found');
    }

    observable
      .pipe(
        rx.takeUntil(this.destroy$),
        rx.tap(() => cdr.markForCheck()),
      )
      .subscribe(subject);

    return subject as any;
  }
}
